import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { useUnreadDMs } from "@/hooks/useUnreadDMs";
import { useFollowingPing } from "@/hooks/useFollowingPing";
import { useSitePresence } from "@/hooks/useSitePresence";
import { useDMSocket } from "@/hooks/useDMSocket";
import { useTrafficSource } from "@/hooks/useTrafficSource";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Toaster } from "@/components/ui/sonner";
import AgeGate from "@/pages/AgeGate";
import AuthPage from "@/pages/Auth";
import Lobby from "@/pages/Lobby";
import Room from "@/pages/Room";
import Profile from "@/pages/Profile";
import DMInbox from "@/pages/DMInbox";
import DMThread from "@/pages/DMThread";
import Mentions from "@/pages/Mentions";
import SharePreview from "@/pages/SharePreview";
import AdminDashboard from "@/pages/AdminDashboard";
import UserProfile from "@/pages/UserProfile";
import SavedMessages from "@/pages/SavedMessages";
import InstallScreen from "@/pages/InstallScreen";
import GenderPrompt from "@/components/GenderPrompt";

function Protected({ children }) {
  const { user } = useAuth();
  useUnreadDMs();
  useFollowingPing();
  useSitePresence();
  useDMSocket();
  usePushNotifications();
  if (user === null) return <div className="min-h-screen flex items-center justify-center font-mono text-acid">LOADING…</div>;
  if (!user) return <Navigate to="/enter" replace />;
  return (
    <>
      {children}
      <GenderPrompt />
    </>
  );
}

function Gate({ children }) {
  const ok = typeof window !== "undefined" && localStorage.getItem("nw_age_ok") === "yes";
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

function TrafficSourceTracker() {
  useTrafficSource();
  return null;
}

function App() {
  useEffect(() => {
    // Register the service worker (skips CRA dev server which doesn't serve /sw.js consistently)
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/sw.js").catch(() => { /* silent */ });
    }
  }, []);

  return (
    <div className="App grain scanlines min-h-screen">
      <ThemeProvider>
        <AuthProvider>
          <TrafficSourceTracker />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<AgeGate />} />
              <Route path="/share/:slug" element={<SharePreview />} />
              <Route path="/enter" element={<Gate><AuthPage /></Gate>} />
              <Route path="/lobby" element={<Gate><Protected><Lobby /></Protected></Gate>} />
              <Route path="/room/:slug" element={<Gate><Protected><Room /></Protected></Gate>} />
              <Route path="/profile" element={<Gate><Protected><Profile /></Protected></Gate>} />
              <Route path="/saved" element={<Gate><Protected><SavedMessages /></Protected></Gate>} />
              <Route path="/install" element={<InstallScreen />} />
              <Route path="/dm" element={<Gate><Protected><DMInbox /></Protected></Gate>} />
              <Route path="/dm/:userId" element={<Gate><Protected><DMThread /></Protected></Gate>} />
              <Route path="/mentions" element={<Gate><Protected><Mentions /></Protected></Gate>} />
              <Route path="/admin" element={<Gate><Protected><AdminDashboard /></Protected></Gate>} />
              <Route path="/u/:username" element={<Gate><Protected><UserProfile /></Protected></Gate>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "var(--nw-bg)",
                border: "2px solid #D4FF00",
                color: "var(--nw-text)",
                borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace",
              },
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
